import React, { useState } from 'react';
import { Box, Tabs, Tab, Paper, Typography, Button, Grid, Chip } from '@mui/material';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import SurgicalPreOpPhase from './SurgicalPreOpPhase';
import SurgicalIntraOpPhase from './SurgicalIntraOpPhase';
import SurgicalPACUPhase from './SurgicalPACUPhase';
import SurgicalPostOpPhase from './SurgicalPostOpPhase';

dayjs.extend(utc);
dayjs.extend(timezone);

interface SurgicalProcedureData {
  id: string;
  procedureType: string;
  specialty: string;
  status: string;
  orderDate: Date;
  estimatedDuration?: number;
  requiresConsent?: boolean;
  specialInstructions?: string;
  orderingProvider?: string;
  priority?: string;
}

interface Patient {
  id: string;
  name: string;
  mrn?: string;
}

interface SurgicalProcedurePanelProps {
  surgicalProcedure: SurgicalProcedureData;
  patient: Patient;
  encounterId?: string;
  fin?: string;
  onBack?: () => void;
  showBackButton?: boolean;
  embedded?: boolean; // Whether this is embedded in a parent component
}

const phaseTabs = [
  { label: 'Pre-Op' },
  { label: 'Intra-Op' },
  { label: 'PACU/Recovery' },
  { label: 'Post-Op/Discharge' },
];

const SurgicalProcedurePanel: React.FC<SurgicalProcedurePanelProps> = ({
  surgicalProcedure,
  patient,
  encounterId,
  fin,
  onBack,
  showBackButton = true,
  embedded = false
}) => {
  const [activePhase, setActivePhase] = useState(0);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActivePhase(newValue);
  };

  const getDisplayDate = () => {
    if (!surgicalProcedure?.orderDate) return 'Unknown Date';
    let date = new Date(surgicalProcedure.orderDate);
    if (isNaN(date.getTime())) return 'Invalid Date';
    const userTz = dayjs.tz.guess();
    return dayjs(date).tz(userTz).format('YYYY-MM-DD HH:mm:ss');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'pending':
        return 'warning';
      case 'ordered':
        return 'info';
      default:
        return 'default';
    }
  };

  const patientName = patient?.name || 'Unknown Patient';
  const procedureDate = getDisplayDate();

  // MRN/FIN label logic
  const mrnValue = patient?.mrn;
  const finValue = fin;
  const mrnDisplay = mrnValue
    ? (mrnValue.toUpperCase().startsWith('MRN') ? mrnValue : `MRN: ${mrnValue}`)
    : 'No MRN';
  const finDisplay = finValue
    ? (finValue.toUpperCase().startsWith('FIN') ? finValue : `FIN: ${finValue}`)
    : 'No FIN';

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {/* Gradient Header */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderRadius: 3,
          p: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 8px 32px rgba(118, 75, 162, 0.3)',
          '&:hover': {
            boxShadow: '0 12px 48px rgba(118, 75, 162, 0.4)',
          },
          transition: 'box-shadow 0.3s ease-in-out',
          color: '#fff',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#fff', textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
            Surgical Procedure - {surgicalProcedure.procedureType}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.95)', mb: 1 }}>
            Patient: {patientName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {procedureDate} • {mrnDisplay} • {finDisplay}
            </Typography>
            <Chip
              label={surgicalProcedure.status}
              color={getStatusColor(surgicalProcedure.status) as any}
              size="small"
              sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&.MuiChip-colorSuccess': {
                  backgroundColor: 'rgba(76, 175, 80, 0.8)',
                },
                '&.MuiChip-colorError': {
                  backgroundColor: 'rgba(244, 67, 54, 0.8)',
                },
                '&.MuiChip-colorWarning': {
                  backgroundColor: 'rgba(255, 152, 0, 0.8)',
                },
                '&.MuiChip-colorInfo': {
                  backgroundColor: 'rgba(33, 150, 243, 0.8)',
                }
              }}
            />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              Specialty: {surgicalProcedure.specialty}
            </Typography>
            {surgicalProcedure.estimatedDuration && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                Duration: {surgicalProcedure.estimatedDuration} min
              </Typography>
            )}
          </Box>
        </Box>
        {showBackButton && onBack && (
          <Button
            variant="contained"
            onClick={onBack}
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.4)',
              }
            }}
          >
            Back to Orders
          </Button>
        )}
      </Box>

      {/* Procedure Details Summary */}
      <Paper elevation={1} sx={{ borderRadius: 3, mb: 3, p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Ordering Provider
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {surgicalProcedure.orderingProvider || 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Priority
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {surgicalProcedure.priority || 'Routine'}
            </Typography>
          </Grid>
          {surgicalProcedure.specialInstructions && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Special Instructions
              </Typography>
              <Typography variant="body1">
                {surgicalProcedure.specialInstructions}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Phase Tabs */}
      <Paper elevation={1} sx={{ borderRadius: 3, mb: 3 }}>
        <Tabs
          value={activePhase}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            pt: 2,
            '& .MuiTab-root': {
              minHeight: 48,
              fontWeight: 500,
              textTransform: 'none',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#708bff',
              height: 3,
            },
          }}
        >
          {phaseTabs.map((tab, idx) => (
            <Tab key={tab.label} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Phase Content */}
      <Box>
        {activePhase === 0 && (
          <SurgicalPreOpPhase 
            surgicalProcedure={surgicalProcedure} 
            patient={patient} 
            encounterId={encounterId}
          />
        )}
        {activePhase === 1 && (
          <SurgicalIntraOpPhase 
            surgicalProcedure={surgicalProcedure} 
            patient={patient} 
            encounterId={encounterId}
          />
        )}
        {activePhase === 2 && (
          <SurgicalPACUPhase 
            surgicalProcedure={surgicalProcedure} 
            patient={patient} 
            encounterId={encounterId}
          />
        )}
        {activePhase === 3 && (
          <SurgicalPostOpPhase 
            surgicalProcedure={surgicalProcedure} 
            patient={patient} 
            encounterId={encounterId}
          />
        )}
      </Box>
    </Box>
  );
};

export default SurgicalProcedurePanel; 